import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Chip,
  Box,
  Button,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../../services/api';

const SchoolProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchProjectDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getSchoolProjectById(id);
      setProject(response.data);
    } catch (err) {
      console.error('Error fetching project details:', err);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'info',
      in_progress: 'warning',
      completed: 'success',
      delivered: 'secondary',
      installed: 'primary',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pending',
      in_progress: 'In Progress',
      completed: 'Completed',
      delivered: 'Delivered',
      installed: 'Installed',
      cancelled: 'Cancelled'
    };
    return texts[status] || status;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 2 }}>
          Project not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {project.project_name}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              label={getStatusText(project.status)}
              color={getStatusColor(project.status)}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Project ID: {project.id}
            </Typography>
          </Box>
        </Box>
        
        <Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/school-projects/projects/edit/${id}`)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/school-projects/projects/${id}/add-computer`)}
          >
            Add Computer
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Computers" />
          <Tab label="Timeline" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Project Details */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Project Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    School
                  </Typography>
                  <Typography variant="body1">
                    {project.School?.school_name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Lab Size
                  </Typography>
                  <Typography variant="body1">
                    {project.lab_size} computers
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(project.start_date).format('DD MMM YYYY')}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Estimated Completion
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(project.estimated_completion_date).format('DD MMM YYYY')}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Progress
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ComputerIcon />
                    <Typography variant="body1">
                      {project.installed_computers || 0} / {project.total_computers || 0} computers installed
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {project.notes || 'No notes'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Stats Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Statistics
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Total Computers"
                      secondary={project.total_computers || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Installed"
                      secondary={project.installed_computers || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Estimated Cost"
                      secondary={`Rs ${project.estimated_cost || 0}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Actual Cost"
                      secondary={`Rs ${project.actual_cost || 0}`}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Computers Tab */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Computers
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/school-projects/projects/${id}/add-computer`)}
            >
              Add Computer
            </Button>
          </Box>
          
          <Alert severity="info">
            Computer management will be implemented soon. You can add computers to track assembly progress.
          </Alert>
        </Paper>
      )}

      {/* Timeline Tab */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Project Timeline
          </Typography>
          <Alert severity="info">
            Timeline view will be implemented here to track project milestones.
          </Alert>
        </Paper>
      )}
    </Container>
  );
};

export default SchoolProjectDetails;